import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { Button } from "@/shared/components/ui/button";
import { Separator } from "@/shared/components/ui/separator";
import logo from "@/assets/images/casa_do_alfaiate_logo.png";
import {
  LayoutDashboard,
  Package,
  Warehouse,
  Users,
  UserCheck,
  FileText,
  Calendar,
  PackageCheck,
  Wrench,
  Tag,
  Percent,
  Calculator,
  Receipt,
  ShoppingBag,
  FolderArchive,
  Eye,
  CalendarPlus,
  PackageOpen,
  GraduationCap,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { Can } from "@/features/auth/components/Can";
import { ACTIONS } from "@/constants/roles";
import { PatchNotesGate } from "@/features/patch-notes";

interface AppLayoutProps {
  children: React.ReactNode;
}

const menuItems = [
  {
    icon: LayoutDashboard,
    label: "Dashboard",
    path: "/dashboard",
    permission: ACTIONS.dashboard.read,
  },
  {
    icon: Package,
    label: "Produtos",
    path: "/produtos",
    permission: ACTIONS.products.read,
  },
  {
    icon: Warehouse,
    label: "Estoque",
    path: "/estoque",
    permission: ACTIONS.stock.read,
  },
  {
    icon: Users,
    label: "Clientes",
    path: "/clientes",
    permission: ACTIONS.clients.read,
  },
  {
    icon: UserCheck,
    label: "Vendedores",
    path: "/vendedores",
    permission: ACTIONS.sellers.read,
  },
  {
    icon: FileText,
    label: "Contratos",
    path: "/contratos",
    permission: ACTIONS.contracts.read,
  },
  {
    icon: Calendar,
    label: "Aluguéis",
    path: "/alugueis",
    permission: ACTIONS.rentals.delete,
  },
  {
    icon: CalendarPlus,
    label: "Novo Aluguel",
    path: "/alugueis/novo",
    permission: ACTIONS.rentals.createUser,
  },
  {
    icon: Eye,
    label: "Ver Aluguéis",
    path: "/ver-alugueis",
    permission: ACTIONS.rentals.viewUser,
  },
  {
    icon: PackageOpen,
    label: "Retirada",
    path: "/retirada",
    permission: ACTIONS.rentals.pickup,
  },
  {
    icon: Tag,
    label: "Tags",
    path: "/tags",
    permission: ACTIONS.tags.read,
  },
  {
    icon: Percent,
    label: "Comissões",
    path: "/comissoes",
    permission: ACTIONS.commissions.read,
  },
  {
    icon: Calculator,
    label: "Taxas de Comissão",
    path: "/taxas-comissao",
    permission: ACTIONS.commissionRates.read,
  },
  {
    icon: Receipt,
    label: "Notas Fiscais",
    path: "/notas-fiscais",
    permission: ACTIONS.invoices.read,
  },
  {
    icon: ShoppingBag,
    label: "Notas Comerciais",
    path: "/notas-comerciais",
    permission: ACTIONS.commercialInvoices.read,
  },
  {
    icon: FolderArchive,
    label: "Exportação Fiscal",
    path: "/exportacao-fiscal",
    permission: ACTIONS.fiscalExport.read,
  },
  {
    icon: GraduationCap,
    label: "Formaturas",
    path: "/formaturas",
    permission: ACTIONS.graduations.read,
  },
  {
    icon: PackageCheck,
    label: "Devolução",
    path: "/devolucoes",
    permission: ACTIONS.devolutions.read,
  },
  {
    icon: Wrench,
    label: "Manutenção",
    path: "/manutencao",
    permission: ACTIONS.maintenances.read,
  },
];

const SIDEBAR_STORAGE_KEY = "sidebar-collapsed";

// "" (env presente mas vazia) também cai no fallback — por isso ||, não ??.
const APP_VERSION = import.meta.env.VITE_APP_VERSION || "dev";

export function AppLayout({ children }: AppLayoutProps) {
  const { logout } = useAuth();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDesktopSidebarCollapsed, setIsDesktopSidebarCollapsed] = useState(
    () => {
      const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
      return stored === "true";
    },
  );
  const [showMobileHeader, setShowMobileHeader] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    localStorage.setItem(
      SIDEBAR_STORAGE_KEY,
      String(isDesktopSidebarCollapsed),
    );
  }, [isDesktopSidebarCollapsed]);

  useEffect(() => {
    const mainElement = mainRef.current;
    if (!mainElement) return;

    const handleScroll = () => {
      const currentScrollY = mainElement.scrollTop;

      if (currentScrollY > lastScrollY && currentScrollY > 50) {
        setShowMobileHeader(false);
      } else if (currentScrollY < lastScrollY) {
        setShowMobileHeader(true);
      }

      setLastScrollY(currentScrollY);
    };

    mainElement.addEventListener("scroll", handleScroll, { passive: true });
    return () => mainElement.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="h-screen bg-background dark flex overflow-hidden">
      <aside
        className={`hidden lg:flex lg:flex-col bg-card border-r border-border h-screen transition-all duration-300 ${
          isDesktopSidebarCollapsed ? "lg:w-16" : "lg:w-64"
        }`}
      >
        <div className="shrink-0 pt-8">
          {!isDesktopSidebarCollapsed ? (
            <div className="flex justify-center">
              <img
                src={logo}
                alt="Logo Casa do Alfaiate"
                className="h-30 w-auto"
              />
            </div>
          ) : null}

          <div
            className={`pb-3 pt-6 ${
              isDesktopSidebarCollapsed ? "pt-6" : "px-6"
            }`}
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setIsDesktopSidebarCollapsed(!isDesktopSidebarCollapsed)
              }
              className={`${
                isDesktopSidebarCollapsed
                  ? "w-10 h-10 p-0 mx-auto flex items-center justify-center"
                  : "w-full"
              }`}
            >
              {isDesktopSidebarCollapsed ? (
                <ChevronRight className="w-4 h-4" />
              ) : (
                <>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  <span className="text-sm">Recolher</span>
                </>
              )}
            </Button>
          </div>
        </div>

        <Separator className="shrink-0" />

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            return (
              <Can key={item.path} permission={item.permission}>
                <Link
                  to={item.path}
                  title={isDesktopSidebarCollapsed ? item.label : undefined}
                  className={`flex items-center ${
                    isDesktopSidebarCollapsed ? "justify-center" : "gap-3"
                  } px-3 py-2 rounded-md text-sm font-medium transition-colors
                  ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  }`}
                >
                  <Icon className="w-5 h-5 shrink-0" />
                  {!isDesktopSidebarCollapsed && <span>{item.label}</span>}
                </Link>
              </Can>
            );
          })}
        </nav>

        <Separator className="shrink-0" />

        <div className="p-4 shrink-0">
          {!isDesktopSidebarCollapsed ? (
            <>
              <Button
                variant="outline"
                size="sm"
                className="w-full gap-2"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4" />
                Sair
              </Button>
              <p className="mt-3 text-center text-xs text-muted-foreground">
                {APP_VERSION}
              </p>
            </>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <Button
                variant="outline"
                size="icon-sm"
                onClick={handleLogout}
                title="Sair"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </aside>

      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <aside
        className={`
        fixed top-0 left-0 bottom-0 w-64 bg-card border-r border-border z-50 lg:hidden flex flex-col
        transform transition-transform duration-300
        ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}
      `}
      >
        <div className="flex items-center justify-between p-6 shrink-0">
          <div className="flex-1 flex justify-center">
            <img
              src={logo}
              alt="Logo Casa do Alfaiate"
              className="h-20 w-auto object-contain"
            />
          </div>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setIsSidebarOpen(false)}
            className="absolute right-4"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        <Separator />

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            return (
              <Can key={item.path} permission={item.permission}>
                <Link
                  to={item.path}
                  onClick={() => setIsSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors
                  ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </Link>
              </Can>
            );
          })}
        </nav>

        <Separator />

        <div className="p-4 shrink-0">
          <Button
            variant="outline"
            size="sm"
            className="w-full gap-2"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4" />
            Sair
          </Button>
          <p className="mt-3 text-center text-xs text-muted-foreground">
            {APP_VERSION}
          </p>
        </div>
      </aside>

      <div className="flex-1 flex flex-col h-screen overflow-hidden relative">
        <header
          className={`lg:hidden bg-card border-b border-border px-4 py-2 flex items-center justify-between fixed top-0 left-0 right-0 z-30 transition-transform duration-300 ${
            showMobileHeader ? "translate-y-0" : "-translate-y-full"
          }`}
        >
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setIsSidebarOpen(true)}
          >
            <Menu className="w-4 h-4" />
          </Button>
          <img
            src={logo}
            alt="Logo Casa do Alfaiate"
            className="h-12.5 w-auto object-contain"
          />
        </header>

        <main ref={mainRef} className="flex-1 overflow-y-auto">
          <div className="p-4 lg:p-8 pt-20 lg:pt-8">{children}</div>
        </main>
      </div>

      <PatchNotesGate />
    </div>
  );
}
